import { AlertTriangle, CheckCircle2, Users } from 'lucide-react';
import { claimLineText, partnerGroupHeading, refusalText } from '@/lib/groupClaim';
import { useT } from '@/lib/i18n';
import type { GroupClaimSummary } from '@/types/sale';

/**
 * ABT-S22 — a partner (travel-agent) group at the ticketbox: who booked it,
 * for which day, how many wristbands, and whether they were already taken.
 * Deliberately shows NO prices: the person at the counter is the agent's
 * customer, not the buyer, and prices would show the agent's cost.
 */
export function GroupClaimPanel({ summary }: { summary: GroupClaimSummary }) {
  const { locale, t } = useT();
  return (
    <div className="space-y-4" data-testid="group-claim-panel">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-ink-400" data-testid="group-claim-heading">
          {partnerGroupHeading(summary, locale)}
        </p>
        <p className="font-display text-xl font-bold text-ink-900">{summary.partnerName ?? '—'}</p>
        <p className="text-sm text-ink-500">
          {t('claim.visitLine', { date: summary.visitDate ?? '—', count: summary.headcount })}
        </p>
      </div>
      <ul className="space-y-2">
        {summary.groups.map((g) => (
          <li key={g.bookingNumber} className="rounded-lg border border-cream-200 px-3 py-2 text-sm">
            <div className="flex items-center justify-between gap-2">
              <span className="font-semibold tnum">{g.bookingNumber}</span>
              <span className="text-ink-500">{t('claim.people', { count: g.headcount })}</span>
            </div>
            {g.claim ? (
              <p className="mt-1 flex items-center gap-1.5 text-green-700">
                <CheckCircle2 className="h-4 w-4 shrink-0" /> {claimLineText(g.claim, locale)}
              </p>
            ) : g.refusal ? (
              <p className="mt-1 flex items-center gap-1.5 text-amber-700">
                <AlertTriangle className="h-4 w-4 shrink-0" /> {refusalText(g.refusal, summary, locale)}
              </p>
            ) : (
              <p className="mt-1 text-ink-500">{t('claim.notClaimed')}</p>
            )}
          </li>
        ))}
      </ul>
      {summary.refusal && !summary.claimable && (
        <p className="flex items-center gap-1.5 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800" role="alert">
          <AlertTriangle className="h-4 w-4 shrink-0" /> {refusalText(summary.refusal, summary, locale)}
        </p>
      )}
      {summary.claimable && (
        <p className="flex items-center gap-2 text-sm text-ink-500">
          <Users className="h-4 w-4 text-ink-400" />
          {t('claim.allAtOnce', { count: summary.headcount })}
        </p>
      )}
    </div>
  );
}
